import type { LegalVerificationStatus } from '@/types/auth';
import { LEGAL_SDK_TOKEN_ERROR_CODES } from '@helpers/Errors';

// Cómo se presenta el estado del trámite de Legal en el panel (CORP-599). El
// estado sale de `get-profile` (lo escribe el webhook) o, como pista mientras
// el SDK está abierto, de su `onStatusChange`; nunca se guarda desde aquí.

// ─── Chip ─────────────────────────────────────────────────────────────────────

export type LegalVerificationTone = 'brand' | 'warning' | 'error';

export interface LegalVerificationChipState {
  label: string;
  tone: LegalVerificationTone;
}

const CHIP_STATES: Record<LegalVerificationStatus, LegalVerificationChipState> =
  {
    in_review: { label: 'En revisión', tone: 'brand' },
    action_required: { label: 'Acción requerida', tone: 'warning' },
    approved: { label: 'Documentos aprobados', tone: 'brand' },
    contract_sent: { label: 'Contrato enviado', tone: 'brand' },
    completed: { label: 'Activando panel', tone: 'brand' },
    closed: { label: 'Trámite cerrado', tone: 'error' },
  };

const NOT_STARTED: LegalVerificationChipState = {
  label: 'Verificación pendiente',
  tone: 'brand',
};

// `hasOwnProperty` y no `in`: `'toString' in CHIP_STATES` también es `true`.
export const isLegalVerificationStatus = (
  status: string | null | undefined,
): status is LegalVerificationStatus =>
  typeof status === 'string' &&
  Object.prototype.hasOwnProperty.call(CHIP_STATES, status);

/** La espera de la activación pasó su tope: el chip deja de decir «Activando». */
export const ACTIVATION_DELAYED_CHIP_STATE: LegalVerificationChipState = {
  label: 'Activación demorada',
  tone: 'warning',
};

/** Estado desconocido (p. ej. uno nuevo de Legal) = sin empezar. */
export const getLegalVerificationChipState = (
  status: string | null | undefined,
): LegalVerificationChipState =>
  isLegalVerificationStatus(status) ? CHIP_STATES[status] : NOT_STARTED;

/**
 * Solo `get-profile` trae el estado del trámite; login, activate-panel y
 * switch-panel no. Si la respuesta no lo reporta y es el mismo panel, se
 * conserva el último valor conocido: sin esto el chip vuelve a «Verificación
 * pendiente» hasta que llega el perfil. Otro panel nunca hereda el estado.
 */
export const resolveLegalVerificationStatus = (
  reported: string | null | undefined,
  previous: { corporateId?: string | null; status?: string | null } | undefined,
  corporateId: string | null | undefined,
): string | null =>
  reported ??
  (corporateId && previous?.corporateId === corporateId
    ? previous.status ?? null
    : null);

// ─── Token errors ─────────────────────────────────────────────────────────────

/** Qué hace el modal ante cada error del endpoint del token. */
export type LegalTokenErrorKind =
  /** 1188: el panel ya está activo → refrescar perfil y cerrar. */
  | 'already_active'
  /**
   * 1189: ya no corresponde abrir un trámite nuevo (el último dejó de estar
   * `closed`) → refrescar perfil y volver a montar sin `new_verification`.
   */
  | 'new_verification_rejected'
  /** 1186 / 1187: el registro no coincide con el trámite → soporte. */
  | 'data_mismatch'
  /** 1190, red o error desconocido → reintentar. */
  | 'retryable'
  /** 1191, 1192: no se resuelve reintentando. */
  | 'blocked';

export interface LegalTokenError {
  kind: LegalTokenErrorKind;
  message: string;
}

const RETRY_MESSAGE =
  'No pudimos cargar la verificación. Revisa tu conexión e intenta de nuevo.';

export const classifyLegalTokenError = (error: unknown): LegalTokenError => {
  const { error_code: code, message } = (error ?? {}) as {
    error_code?: unknown;
    message?: unknown;
  };
  const text =
    typeof message === 'string' && message.length > 0 ? message : RETRY_MESSAGE;

  switch (String(code)) {
    case LEGAL_SDK_TOKEN_ERROR_CODES.ALREADY_ACTIVE:
      return { kind: 'already_active', message: text };
    case LEGAL_SDK_TOKEN_ERROR_CODES.DATA_MISMATCH:
    case LEGAL_SDK_TOKEN_ERROR_CODES.VALIDATION_FAILED:
      return { kind: 'data_mismatch', message: text };
    case LEGAL_SDK_TOKEN_ERROR_CODES.NEW_VERIFICATION_NOT_ALLOWED:
      return { kind: 'new_verification_rejected', message: text };
    case LEGAL_SDK_TOKEN_ERROR_CODES.RIF_INVALID:
    case LEGAL_SDK_TOKEN_ERROR_CODES.NOT_ALLOWED:
      return { kind: 'blocked', message: text };
    default:
      return { kind: 'retryable', message: code ? text : RETRY_MESSAGE };
  }
};
